import React, { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import Dropzone from "react-dropzone";
import ApiDatePickerField from "../../../components/date/ApiDatePickerField";

import {
  CLASSIFICATION_ALL,
  SALUTATIONS,
  resolveServiceGroupForRole,
  filterClassificationsForGroup,
  getDistricts,
} from "../api/diagnosticDoctorApi";
import { normalizeDiagnosticUserType } from "../utils/diagnosticUserFormShared";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

/**
 * Extra fields for add_new_user/ when user_type is Radiologist or Pathologist.
 * Expects react-hook-form control/register/watch/setValue from parent.
 */
export default function DiagnosticUserFields({
  register,
  watch,
  setValue,
  errors = {},
  lookups = { groups: [], classifications: [] },
  states = [],
  attachments,
  setAttachments,
  control,
}) {
  const userType = watch("user_type");
  const serviceGroupId = watch("service_group_id", "");
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    if (!userType) {
      setValue("service_group_id", "");
      setValue("service_classification_id", CLASSIFICATION_ALL);
      return;
    }
    const roleType = normalizeDiagnosticUserType(userType).toLowerCase();
    const matched = resolveServiceGroupForRole(roleType, lookups.groups);
    setValue("service_group_id", matched?.id ? String(matched.id) : "");
    setValue("service_classification_id", CLASSIFICATION_ALL);
  }, [userType, lookups.groups, setValue]);

  useEffect(() => {
    setValue("service_classification_id", CLASSIFICATION_ALL);
  }, [serviceGroupId, setValue]);

  const groupClassifications = useMemo(
    () =>
      filterClassificationsForGroup(
        lookups.classifications,
        serviceGroupId ? Number(serviceGroupId) : null,
      ),
    [lookups.classifications, serviceGroupId],
  );

  const handleStateChange = (stateName) => {
    setDistricts([]);
    setValue("district", "");
    setValue("city", "");
    if (!stateName) return;
    getDistricts(stateName)
      .then(setDistricts)
      .catch((err) => console.error("getDistricts:", err));
  };

  const handleFileSelect = (acceptedFiles) => {
    Promise.all(
      acceptedFiles.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                base64: reader.result,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }),
      ),
    )
      .then((newAttachments) => setAttachments(newAttachments))
      .catch((err) => console.error("Error reading signature:", err));
  };

  return (
    <>
      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="phone_no" className="required-label">
          Phone No
        </label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Phone No"
            {...register("phone_no", { required: "Phone No is required." })}
          />
          {errors.phone_no && (
            <p className="errorMsg" style={{ color: "red" }}>
              {errors.phone_no.message}
            </p>
          )}
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="registration_no">Registration No</label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Registration No"
            {...register("registration_no")}
          />
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="specialization">Specialization</label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Specialization"
            {...register("specialization")}
          />
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="qualifications">Qualifications</label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Qualifications"
            {...register("qualifications")}
          />
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="gender" className="required-label">
          Gender
        </label>
        <div className="form-group">
          <select
            className="form-control"
            {...register("gender", { required: "Gender is required." })}
          >
            <option value="">Select Gender</option>
            {GENDERS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
          {errors.gender && (
            <p className="errorMsg" style={{ color: "red" }}>
              {errors.gender.message}
            </p>
          )}
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="service_group_id" className="required-label">
          Service Group
        </label>
        <div className="form-group">
          <select
            className="form-control"
            {...register("service_group_id", {
              required: "Service Group is required.",
            })}
          >
            <option value="">Select Service Group</option>
            {lookups.groups.map((group) => (
              <option key={group.id} value={String(group.id)}>
                {group.name} ({group.code})
              </option>
            ))}
          </select>
          {errors.service_group_id && (
            <p className="errorMsg" style={{ color: "red" }}>
              {errors.service_group_id.message}
            </p>
          )}
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="service_classification_id">Service Classification</label>
        <div className="form-group">
          <select
            className="form-control"
            disabled={!serviceGroupId}
            {...register("service_classification_id")}
          >
            <option value={CLASSIFICATION_ALL}>All</option>
            {groupClassifications.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.name} ({item.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="state">State</label>
        <div className="form-group">
          <select
            className="form-control"
            {...register("state")}
            onChange={(e) => {
              register("state").onChange(e);
              handleStateChange(e.target.value);
            }}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.state_name || state} value={state.state_name || state}>
                {state.state_name || state}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="district">District</label>
        <div className="form-group">
          <select className="form-control" {...register("district")}>
            <option value="">Select District</option>
            {districts.map((district) => (
              <option
                key={district.district_name || district}
                value={district.district_name || district}
              >
                {district.district_name || district}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="city">City</label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter City"
            {...register("city")}
          />
        </div>
      </div>

      <div className="col-sm-12 col-md-12 col-lg-12">
        <label htmlFor="address" className="required-label">
          Address
        </label>
        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Enter full address"
            {...register("address", {
              required: "Address is required.",
            })}
          />
          {errors.address && (
            <p className="errorMsg" style={{ color: "red" }}>
              {errors.address.message}
            </p>
          )}
        </div>
      </div>

      <div className="col-sm-6 col-md-6 col-lg-6">
        <label htmlFor="date_joined">Date Joined</label>
        <div className="form-group form-group-date">
          <Controller
            name="date_joined"
            control={control}
            render={({ field }) => (
              <ApiDatePickerField
                id="diagnostic-user-date-joined"
                value={field.value || ""}
                onChange={field.onChange}
                className="form-control"
              />
            )}
          />
        </div>
      </div>

      <div className="col-sm-12 col-md-12 col-lg-12">
        <label htmlFor="doctor_signature">Doctor Signature</label>
        <Dropzone onDrop={handleFileSelect} accept={{ "image/*": [] }}>
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className="form-control"
              style={{ padding: "20px", textAlign: "center", cursor: "pointer" }}
            >
              <input {...getInputProps()} />
              {attachments?.length
                ? `Selected: ${attachments.map((f) => f.name).join(", ")}`
                : "Drop signature image here or click to select"}
            </div>
          )}
        </Dropzone>
      </div>
    </>
  );
}
